import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import cmtItemvenddescriptionsService from "../../../services/legacy/cmtItemvenddescriptionsService";
import itemsService from "../../../services/legacy/itemsService";
import salesOrderLinesService from "../../../services/legacy/salesOrderLinesService";
import useDraggableModal from "../../../hooks/useDraggableModal";

const overlay = { position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center" };
const modal = { width: "min(1100px, 94vw)", maxHeight: "88vh", overflow: "auto", background: "white", borderRadius: 8, padding: 20, boxShadow: "0 20px 55px rgba(0,0,0,.3)" };
const dragHandle = { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "move", touchAction: "none", userSelect: "none" };

export default function SalesLineItemSearchModal({ dataAreaId, iSalesId, onClose, onQueued }) {
  const { t } = useTranslation();
  const [lines, setLines] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [lineKey, setLineKey] = useState("");
  const [query, setQuery] = useState("");
  const [selectedRecId, setSelectedRecId] = useState("");
  const [existingItems, setExistingItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gramaje, setGramaje] = useState("");
  const [width, setWidth] = useState("");
  const [depth, setDepth] = useState("");

  useEffect(() => {
    Promise.all([
      salesOrderLinesService.getAll(0, 200, [{ field: "lineNum", direction: "asc" }], { dataAreaId, iSalesId }),
      cmtItemvenddescriptionsService.getAll(0, 500, [{ field: "salesitemname", direction: "asc" }], { dataAreaId, obsoleto: 0 }),
    ]).then(([linePage, materialPage]) => {
      const orderLines = linePage?.content || [];
      setLines(orderLines);
      setLineKey(orderLines[0] ? JSON.stringify(orderLines[0].id || orderLines[0]) : "");
      setMaterials(materialPage?.content || []);
    });
  }, [dataAreaId, iSalesId]);

  const selectedLine = useMemo(() => lines.find((line) => JSON.stringify(line.id || line) === lineKey), [lineKey, lines]);
  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    return materials.filter((item) => !value || [item.salesitemname, item.venditemname, item.inventtypeitemid, item.inventvenditemid]
      .some((field) => String(field || "").toLowerCase().includes(value)));
  }, [materials, query]);
  const selectedMaterial = materials.find((item) => String(item.recId) === selectedRecId);

  useEffect(() => {
    if (!selectedLine) return;
    setGramaje(String(selectedLine.gramajeitemid ?? ""));
    setWidth(String(selectedLine.width ?? ""));
    setDepth(String(selectedLine.depth ?? ""));
  }, [selectedLine]);

  useEffect(() => {
    if (!selectedMaterial || !gramaje || !width) { setExistingItems([]); return; }
    itemsService.getAll(0, 50, [], {
      dataAreaId,
      inventtypeitemid: selectedMaterial.inventtypeitemid,
      inventvenditemid: selectedMaterial.inventvenditemid,
      inventgramajeitemid: gramaje,
      width,
      depth,
    }).then((page) => setExistingItems(page?.content || [])).catch(() => setExistingItems([]));
  }, [dataAreaId, depth, gramaje, selectedMaterial, width]);

  const queueCreation = async () => {
    if (!selectedMaterial || !gramaje || !width || busy) return;
    setBusy(true);
    try {
      await itemsService.requestCreation({
        dataAreaId,
        typeItemId: selectedMaterial.inventtypeitemid,
        gramajeItemId: gramaje,
        vendItemId: selectedMaterial.inventvenditemid,
        width,
        depth: depth || 0,
        iSalesId,
      });
      onQueued();
    } finally { setBusy(false); }
  };

  const useExisting = async () => {
    if (!selectedLine || !existingItems[0] || busy) return;
    setBusy(true);
    try {
      if (String(selectedLine.itemId || "").toUpperCase().endsWith("XXXX")) {
        await salesOrderLinesService.resolvePlaceholderItem(selectedLine.id || selectedLine, existingItems[0].itemId);
      } else {
        await salesOrderLinesService.changeMaterial(selectedLine.id || selectedLine, existingItems[0].itemId);
      }
      onQueued();
    }
    finally { setBusy(false); }
  };

  const { contentRef, dragHandleProps, contentStyle } = useDraggableModal(true);

  return <div style={overlay} role="dialog" aria-modal="true">
    <div ref={contentRef} style={{ ...modal, ...contentStyle }}>
      <div style={dragHandle} {...dragHandleProps}>
        <h3>{t("salesLines.itemSearch.title")}</h3>
        <button className="btn btn-default" onClick={onClose}>×</button>
      </div>
      <div className="row" style={{ marginBottom: 14 }}>
        <div className="col-md-5"><label>{t("salesLines.itemSearch.targetLine")}</label>
          <select className="form-control" value={lineKey} onChange={(e) => setLineKey(e.target.value)}>
            <option value="">—</option>
            {lines.map((line) => <option key={JSON.stringify(line.id || line)} value={JSON.stringify(line.id || line)}>{line.itemId} — {line.name} ({line.width} × {line.depth})</option>)}
          </select>
        </div>
        <div className="col-md-7"><label>{t("salesLines.itemSearch.search")}</label><input className="form-control" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
      </div>
      <div className="row" style={{ marginBottom: 14 }}>
        <div className="col-md-4"><label>{t("salesLines.gramajeitemid")}</label><input className="form-control" value={gramaje} onChange={(e) => setGramaje(e.target.value)} /></div>
        <div className="col-md-4"><label>{t("salesLines.width")}</label><input type="number" className="form-control" value={width} onChange={(e) => setWidth(e.target.value)} /></div>
        <div className="col-md-4"><label>{t("salesLines.depth")}</label><input type="number" className="form-control" value={depth} onChange={(e) => setDepth(e.target.value)} /></div>
      </div>
      <div style={{ maxHeight: 330, overflow: "auto" }}><table className="table table-striped table-hover"><thead><tr>
        <th></th><th>{t("salesLines.itemSearch.type")}</th><th>{t("salesLines.itemSearch.vendor")}</th><th>{t("salesLines.itemSearch.name")}</th><th>{t("salesLines.itemSearch.maxWeight")}</th><th>{t("salesLines.itemSearch.days")}</th>
      </tr></thead><tbody>{filtered.map((item) => <tr key={item.recId} onClick={() => setSelectedRecId(String(item.recId))} style={{ cursor: "pointer" }}>
        <td><input type="radio" checked={String(item.recId) === selectedRecId} readOnly /></td><td>{item.inventtypeitemid}</td><td>{item.inventvenditemid}</td><td>{item.salesitemname || item.venditemname}</td><td>{item.inventgramajeitemidto}</td><td>{item.deliverydays}</td>
      </tr>)}</tbody></table></div>
      {selectedMaterial ? <div className="alert alert-info">{existingItems.length > 0
        ? t("salesLines.itemSearch.existing", { itemId: existingItems[0].itemId })
        : t("salesLines.itemSearch.notExisting")}</div> : null}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><button className="btn btn-default" onClick={onClose}>{t("common.cancel")}</button>
        {existingItems.length > 0 && selectedLine ? <button className="btn btn-primary" disabled={busy} onClick={useExisting}>{t("common.select")}</button>
          : existingItems.length === 0 ? <button className="btn btn-primary" disabled={!selectedMaterial || !gramaje || !width || busy} onClick={queueCreation}>{t("salesLines.materialAlternatives.createItem")}</button> : null}</div>
    </div>
  </div>;
}
